import manifest from './corsPublicRoutes.json'

/**
 * Core's public routes, copied verbatim from `contracts/cors-public-routes.json`
 * in MulberryHQ/core.
 *
 * A route is public when core answers a cross-origin preflight with
 * `Access-Control-Allow-Origin: *` and no `Access-Control-Allow-Credentials`.
 * A browser refuses to expose such a response to a credentialed request, so
 * these must go through `publicAxios` and everything else must not.
 *
 * Core regenerates its copy with `make update-cors-manifest`, and a test there
 * fails if it drifts from the route map. This copy is the part that can go
 * stale, which is why `corsClientGuard.test.js` exists.
 */
export const PUBLIC_ROUTES = manifest.routes

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// `{param}` matches exactly one segment, so `/api/d2c/carts/{cart_id}` matches
// `/api/d2c/carts/abc` but not `/api/d2c/carts/abc/items` -- a different route,
// which may well have a different CORS profile.
const ROUTE_MATCHERS = PUBLIC_ROUTES.map(
  (route) =>
    new RegExp(
      `^${route
        .split(/(\{[^}]+\})/)
        .map((part) =>
          part.startsWith('{') && part.endsWith('}')
            ? '[^/]+'
            : escapeRegExp(part)
        )
        .join('')}$`
    )
)

/**
 * Reduce a request URL to the path the manifest is keyed by.
 *
 * Unlike explore, this app's baseURL is the host root, so call sites already
 * carry the `/api` prefix themselves -- sometimes with a leading slash
 * (`/api/warranty-offers`) and sometimes without (`api/auditor-results`). Both
 * resolve to the same URL, so both must normalise to the same path. Paths
 * outside `/api` (`/util/reset_reg`) are left alone and simply match nothing,
 * which is correct: they are not public routes.
 */
export const normalisePath = (url) => {
  const withoutOrigin = url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '')
  const [pathOnly = ''] = withoutOrigin.split(/[?#]/)
  return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`
}

export const isPublicPath = (url) => {
  const path = normalisePath(url)
  return ROUTE_MATCHERS.some((matcher) => matcher.test(path))
}
